import React, { useEffect, useState, useMemo } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { motion } from "framer-motion";
import { GraduationCap, Users, MessageCircle, Sparkles, Award, Wallet, CheckCircle2 } from "lucide-react";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";
import ChatTab from "@/pages/pm/tabs/ChatTab";

export default function PrincipalDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [data, setData] = useState({ committees: [], allotments: [], budget: null, posts: [] });
  const [feeSummary, setFeeSummary] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/events");
      setEvents(r.data);
      if (r.data[0]) setSelectedEvent(r.data[0]);
      try {
        const s = await api.get("/principal/fee-summary");
        setFeeSummary(s.data || []);
      } catch { /* endpoint might not exist yet */ }
    })();
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    (async () => {
      const [c, a, p] = await Promise.all([
        api.get(`/events/${selectedEvent.event_id}/committees`),
        api.get(`/events/${selectedEvent.event_id}/allotments`),
        api.get(`/events/${selectedEvent.event_id}/social-posts`).catch(() => ({ data: [] })),
      ]);
      setData({ committees: c.data, allotments: a.data, budget: null, posts: p.data });
    })();
  }, [selectedEvent]);

  const countdown = useMemo(() => {
    if (!selectedEvent) return null;
    const target = new Date(selectedEvent.start_date).getTime();
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((diff % (1000 * 60)) / 1000);
    return { d, h, m, s, started: diff === 0 };
  }, [selectedEvent, now]);

  const pieData = data.committees.map((c, i) => ({ name: c.name, value: data.allotments.filter((a) => a.committee_id === c.committee_id).length }));
  const colors = ["#F97316", "#EC4899", "#14B8A6", "#6366F1", "#EAB308", "#8B5CF6", "#22C55E"];

  // Staff + Checklist state (lifted from selectedEvent)
  const checklists = selectedEvent?.principal_checklists || [];
  const staff = selectedEvent?.staff_allocations || [];
  const [newStaff, setNewStaff] = useState({ name: "", position: "", contact: "" });
  const [newChecklist, setNewChecklist] = useState("");

  const refreshEvent = async () => {
    const r = await api.get(`/events/${selectedEvent.event_id}`);
    setSelectedEvent(r.data);
  };

  const addStaff = async (e) => {
    e.preventDefault();
    if (!newStaff.name.trim() || !newStaff.position.trim()) return;
    await api.post(`/events/${selectedEvent.event_id}/staff-allocations`, newStaff);
    setNewStaff({ name: "", position: "", contact: "" });
    refreshEvent();
  };
  const delStaff = async (sid) => {
    await api.delete(`/events/${selectedEvent.event_id}/staff-allocations/${sid}`);
    refreshEvent();
  };
  const addChecklist = async () => {
    if (!newChecklist.trim()) return;
    const items = [...checklists, { label: newChecklist.trim(), done: false }];
    await api.put(`/events/${selectedEvent.event_id}/checklists`, { checklists: items });
    setNewChecklist("");
    refreshEvent();
  };
  const toggleChecklist = async (cid) => {
    await api.post(`/events/${selectedEvent.event_id}/checklists/${cid}/toggle`);
    refreshEvent();
  };

  if (!selectedEvent) {
    return <Layout navItems={getNav("school_principal")}><div className="p-12 text-slate-400">No events assigned to you yet.</div></Layout>;
  }

  return (
    <Layout navItems={getNav("school_principal")}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        {/* Hero card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl p-8 mb-8 text-white"
          style={{ background: "linear-gradient(135deg, #F97316 0%, #EC4899 50%, #8B5CF6 100%)" }}>
          <div className="blob bg-yellow-300 animate-float" style={{ width: 300, height: 300, top: -100, right: -50, opacity: 0.3 }} />
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            {selectedEvent.logo_url ? (
              <img src={`${BACKEND_URL}${selectedEvent.logo_url}`} className="w-24 h-24 rounded-2xl object-cover bg-white/20 backdrop-blur" alt="" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center font-display font-black text-4xl">{selectedEvent.name.charAt(0)}</div>
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider opacity-80">Hosting</div>
              <h1 className="font-display text-4xl md:text-5xl font-black">{selectedEvent.name}</h1>
              <div className="opacity-90 mt-1">{selectedEvent.school_name} · {selectedEvent.start_date} → {selectedEvent.end_date}</div>
            </div>
            {countdown && !countdown.started && (
              <div className="grid grid-cols-4 gap-2">
                {[{ k: "d", v: countdown.d }, { k: "h", v: countdown.h }, { k: "m", v: countdown.m }, { k: "s", v: countdown.s }].map((t) => (
                  <div key={t.k} className="bg-white/20 backdrop-blur rounded-xl p-3 text-center min-w-[64px]">
                    <div className="text-2xl font-display font-black">{String(t.v).padStart(2, "0")}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{t.k === "d" ? "days" : t.k === "h" ? "hours" : t.k === "m" ? "mins" : "secs"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {events.length > 1 && (
          <select className="input-pill mb-5 max-w-xs" value={selectedEvent.event_id} onChange={(e) => setSelectedEvent(events.find((x) => x.event_id === e.target.value))}>
            {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.name}</option>)}
          </select>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Delegates" value={data.allotments.length} icon={Users} color="orange" />
          <StatCard label="Committees" value={data.committees.length} icon={Sparkles} color="pink" />
          <StatCard label="Staff Allocated" value={staff.length} icon={Users} color="teal" />
          <StatCard label="Tasks Done" value={`${checklists.filter((c) => c.done).length}/${checklists.length}`} icon={Award} color="indigo" />
        </div>

        {(() => {
          const fee = feeSummary.find((f) => f.event_id === selectedEvent.event_id);
          if (!fee) return null;
          const pct = fee.total_registered ? Math.round((fee.students_paid / fee.total_registered) * 100) : 0;
          const feePct = fee.students_paid ? Math.round((fee.students_fee_transferred / fee.students_paid) * 100) : 0;
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="principal-fee-widgets">
              <div className="dashboard-card p-5 border-l-4 border-emerald-400 bg-emerald-50/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
                  <div>
                    <div className="text-[10px] uppercase font-bold text-emerald-800">Students Paid</div>
                    <div className="font-display font-black text-2xl text-emerald-700">{fee.students_paid} <span className="text-slate-400 text-lg font-bold">/ {fee.total_registered || "—"}</span></div>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-emerald-100 overflow-hidden"><div style={{ width: `${pct}%` }} className="h-full bg-emerald-500 transition-all" /></div>
                <div className="text-[11px] text-slate-500 mt-1.5">{pct}% of registered delegates from your school have paid the fee.</div>
              </div>
              <div className="dashboard-card p-5 border-l-4 border-orange-400 bg-orange-50/40">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
                  <div>
                    <div className="text-[10px] uppercase font-bold text-orange-800">Fee received from Kaimera</div>
                    <div className="font-display font-black text-2xl text-orange-700">{fee.students_fee_transferred} <span className="text-slate-400 text-lg font-bold">/ {fee.students_paid} students</span></div>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-orange-100 overflow-hidden"><div style={{ width: `${feePct}%` }} className="h-full bg-orange-500 transition-all" /></div>
                <div className="text-[11px] text-slate-500 mt-1.5">₹{(fee.fee_transferred_paise / 100).toLocaleString("en-IN")} transferred so far · {Math.max(fee.students_paid - fee.students_fee_transferred, 0)} student{fee.students_paid - fee.students_fee_transferred === 1 ? "" : "s"} pending.</div>
              </div>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          <div className="dashboard-card p-6 lg:col-span-2">
            <h3 className="font-display font-bold text-lg mb-4">Allotments by Committee</h3>
            {pieData.length === 0 ? <div className="text-slate-400 text-sm">No allotments yet.</div> :
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {pieData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>}
          </div>
          <div className="dashboard-card p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50">
            <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Social Calendar</h3>
            <div className="grid grid-cols-3 gap-2 text-center mt-3">
              <div className="p-2 rounded-xl bg-white/70"><div className="text-xl font-black text-pink-700">{data.posts.length}</div><div className="text-[10px] text-pink-700 uppercase">Planned</div></div>
              <div className="p-2 rounded-xl bg-white/70"><div className="text-xl font-black text-emerald-700">{data.posts.filter((p) => p.posted).length}</div><div className="text-[10px] text-emerald-700 uppercase">Posted</div></div>
              <div className="p-2 rounded-xl bg-white/70"><div className="text-xl font-black text-amber-700">{data.posts.filter((p) => !p.posted).length}</div><div className="text-[10px] text-amber-700 uppercase">Pending</div></div>
            </div>
            <button onClick={() => setShowChat(true)} className="btn-primary mt-4 w-full py-2.5 text-sm" data-testid="principal-chat-btn"><MessageCircle className="w-4 h-4" /> Chat with PM</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 mb-8">
          {/* CHECKLISTS */}
          <div className="dashboard-card p-6" data-testid="principal-checklists">
            <h3 className="font-display font-bold text-lg mb-3">Checklists</h3>
            <p className="text-xs text-slate-500 mb-3">Mark items done — Project Manager &amp; Regional PM see the live status.</p>
            <div className="space-y-2">
              {checklists.length === 0 && <div className="text-xs text-slate-400 italic">No checklist items yet — add some below.</div>}
              {checklists.map((c) => (
                <div key={c.id} className={`flex items-center gap-2 p-2 rounded-lg ${c.done ? "bg-emerald-50" : "bg-slate-50"}`}>
                  <button onClick={() => toggleChecklist(c.id)} className={`w-5 h-5 rounded border-2 flex-shrink-0 ${c.done ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`} data-testid={`checklist-toggle-${c.id}`}>
                    {c.done && <Award className="w-3 h-3 text-white" />}
                  </button>
                  <span className={`flex-1 text-sm ${c.done ? "line-through text-slate-400" : ""}`}>{c.label}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input value={newChecklist} onChange={(e) => setNewChecklist(e.target.value)} placeholder="Add a checklist item…" className="input-pill text-sm" data-testid="checklist-input" />
              <button onClick={addChecklist} className="btn-primary px-4 text-sm" data-testid="checklist-add">+</button>
            </div>
          </div>

          {/* STAFF ALLOCATION */}
          <div className="dashboard-card p-6" data-testid="principal-staff">
            <h3 className="font-display font-bold text-lg mb-3">Staff Allocation</h3>
            <p className="text-xs text-slate-500 mb-3">Add team members from your school + their position for this event.</p>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {staff.length === 0 && <div className="text-xs text-slate-400 italic">No staff added yet.</div>}
              {staff.map((s) => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.position}{s.contact ? ` · ${s.contact}` : ""}</div>
                  </div>
                  <button onClick={() => delStaff(s.id)} className="text-rose-500 text-xs">Remove</button>
                </div>
              ))}
            </div>
            <form onSubmit={addStaff} className="mt-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input className="input-pill text-sm" placeholder="Staff name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="staff-name" />
                <input className="input-pill text-sm" placeholder="Position" value={newStaff.position} onChange={(e) => setNewStaff({ ...newStaff, position: e.target.value })} data-testid="staff-position" />
              </div>
              <input className="input-pill text-sm" placeholder="Contact (optional)" value={newStaff.contact} onChange={(e) => setNewStaff({ ...newStaff, contact: e.target.value })} />
              <button type="submit" className="btn-primary w-full py-2 text-sm" data-testid="staff-add">Add Staff</button>
            </form>
          </div>
        </div>

        <div className="grid lg:grid-cols-1 gap-5 mb-8">
          <div className="dashboard-card p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Committees & Agendas</h3>
            </div>
            <div className="space-y-2">
              {data.committees.map((c) => (
                <div key={c.committee_id} className="p-3 rounded-xl bg-slate-50">
                  <div className="font-bold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.agenda || "Agenda pending"}</div>
                  <div className="text-xs mt-1">
                    <span className="text-orange-700">{data.allotments.filter((a) => a.committee_id === c.committee_id).length} delegates allotted</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showChat && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl max-w-2xl w-full relative">
            <ModalCloseBtn onClose={() => setShowChat(false)} />
              <ChatTab event={selectedEvent} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
